/**
 * Barra de filtros de *Control de Precios*.
 *
 * Se queda **pegada arriba** al bajar por la pantalla: ubicación, fecha,
 * búsqueda y categoría son los mismos filtros para las cuatro secciones, y
 * debajo se resume lo que se está viendo (cambios, competencia y alertas) para no
 * tener que bajar a buscarlo.
 */

import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Minus,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { cn, formatNumber } from '@/lib/utils';

import { CONTROL } from '../pricing-utils';

/** Contadores que resume la barra. */
export interface ResumenPrecios {
  cambios: number;
  aumentos: number;
  bajadas: number;
  nuevos: number;
  quitados: number;
  competidores: number;
  alertas: number;
}

interface PreciosFiltrosProps {
  ubicaciones: string[];
  ubicacion: string;
  onUbicacion: (valor: string) => void;
  fecha: string;
  fechaMaxima: string;
  onFecha: (valor: string) => void;
  busqueda: string;
  onBusqueda: (valor: string) => void;
  categorias: string[];
  categoria: string;
  onCategoria: (valor: string) => void;
  onActualizar: () => void;
  actualizando: boolean;
  resumen: ResumenPrecios;
  /** Sección que muestra los contadores al pulsarlos. */
  onVerCambios: () => void;
  onVerCompetencia: () => void;
}

/** Contador pulsable del resumen. */
function Cuenta({
  etiqueta,
  valor,
  icono: Icono,
  tono,
  onClick,
}: {
  etiqueta: string;
  valor: number;
  icono: typeof Search;
  tono: 'neutral' | 'danger' | 'success' | 'brand' | 'warning';
  onClick: () => void;
}) {
  const colores: Record<string, string> = {
    neutral: 'text-ink-500',
    danger: 'text-rose-600',
    success: 'text-emerald-600',
    brand: 'text-brand-600',
    warning: 'text-amber-600',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-control border border-ink-100 bg-white px-2.5 py-1 text-xs transition-colors hover:border-brand-200 hover:bg-brand-50/40"
      title={`Ver ${etiqueta.toLowerCase()}`}
    >
      <Icono className={cn('size-3.5', colores[tono])} aria-hidden="true" />
      <span className="text-ink-500">{etiqueta}</span>
      <span className="font-semibold text-ink-900">{formatNumber(valor)}</span>
    </button>
  );
}

export function PreciosFiltros({
  ubicaciones,
  ubicacion,
  onUbicacion,
  fecha,
  fechaMaxima,
  onFecha,
  busqueda,
  onBusqueda,
  categorias,
  categoria,
  onCategoria,
  onActualizar,
  actualizando,
  resumen,
  onVerCambios,
  onVerCompetencia,
}: PreciosFiltrosProps) {
  return (
    <div className="sticky top-0 z-20 -mx-1 space-y-3 rounded-card border border-ink-100 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pr-ubicacion" className="text-xs font-semibold text-ink-500">
            Ubicación
          </label>
          <Select
            id="pr-ubicacion"
            className={cn(CONTROL, 'h-10 w-64')}
            value={ubicacion}
            onChange={(evento) => onUbicacion(evento.target.value)}
          >
            <option value="">Seleccionar ubicación…</option>
            {ubicaciones.map((item) => (
              <option key={item} value={item}>
                {item.replace(/_/g, ' / ')}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="pr-fecha" className="text-xs font-semibold text-ink-500">
            Fecha
          </label>
          <CampoFecha
            id="pr-fecha"
           
            className={cn(CONTROL, 'h-10 w-40')}
            value={fecha}
            max={fechaMaxima}
            onChange={(evento) => onFecha(evento.target.value)}
           tipo="date" />
        </div>

        <div className="flex min-w-56 flex-1 flex-col gap-1.5">
          <label htmlFor="pr-buscar" className="text-xs font-semibold text-ink-500">
            Buscar estación o producto
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
              aria-hidden="true"
            />
            <input
              id="pr-buscar"
              className={cn(CONTROL, 'h-10 pl-9')}
              placeholder="E.S LAURIAMA, Gasohol, DB5…"
              value={busqueda}
              onChange={(evento) => onBusqueda(evento.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="pr-categoria" className="text-xs font-semibold text-ink-500">
            Categoría
          </label>
          <Select
            id="pr-categoria"
            className={cn(CONTROL, 'h-10 w-56')}
            value={categoria}
            onChange={(evento) => onCategoria(evento.target.value)}
          >
            <option value="">Todas las categorías</option>
            {categorias.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </div>

        <Button
          variant="secondary"
          className="h-10"
          onClick={onActualizar}
          disabled={!ubicacion || actualizando}
        >
          <RefreshCw
            className={cn('mr-1.5 size-3.5', actualizando && 'animate-spin')}
            aria-hidden="true"
          />
          {actualizando ? 'Comparando…' : 'Comparar'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-ink-50 pt-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
          <SlidersHorizontal className="size-3.5" aria-hidden="true" />
          Resumen
        </span>
        <Cuenta
          etiqueta="Cambios"
          valor={resumen.cambios}
          icono={AlertTriangle}
          tono="brand"
          onClick={onVerCambios}
        />
        <Cuenta
          etiqueta="Subieron"
          valor={resumen.aumentos}
          icono={ArrowUpRight}
          tono="danger"
          onClick={onVerCambios}
        />
        <Cuenta
          etiqueta="Bajaron"
          valor={resumen.bajadas}
          icono={ArrowDownRight}
          tono="success"
          onClick={onVerCambios}
        />
        <Cuenta
          etiqueta="Nuevos"
          valor={resumen.nuevos}
          icono={Plus}
          tono="brand"
          onClick={onVerCambios}
        />
        <Cuenta
          etiqueta="Quitados"
          valor={resumen.quitados}
          icono={Minus}
          tono="warning"
          onClick={onVerCambios}
        />
        <Cuenta
          etiqueta="En seguimiento"
          valor={resumen.competidores}
          icono={Search}
          tono="neutral"
          onClick={onVerCompetencia}
        />
        <Cuenta
          etiqueta="Alertas sin leer"
          valor={resumen.alertas}
          icono={AlertTriangle}
          tono={resumen.alertas > 0 ? 'warning' : 'neutral'}
          onClick={onVerCompetencia}
        />
      </div>
    </div>
  );
}
